import type { Ring } from '../../../../geo/types';
import type { VertexStyle } from '../../../mesh/meshBuilder';
import { pointedArchRise, pointedWindow } from '../../panels';
import type { MasonryStyles } from '../masonry';
import type { ButtressSite } from '../oudeKerk/oudeKerkButtress';
import type { NieuweKerkPlan } from './nieuweKerkPlan';

/**
 * Everything the Nieuwe Kerk's parts are built with: `brick` for the walls built after the fire of
 * 1452 and the radiating chapels, banded with stone, and `stone` for the older walls to the east and
 * south, of tuff (De Gids 1942, p. 31), which are drawn plain (surfacePatterns.ts).
 */
export interface NieuweKerkBuild extends ButtressSite {
  readonly plan: NieuweKerkPlan;
  readonly brick: MasonryStyles;
  readonly lead: VertexStyle;
}

/** A window band: its sill and the top of its pointed head, and the window's width. */
export interface Lights {
  readonly sill: number;
  readonly top: number;
  readonly width: number;
}

/**
 * The windows' sizes are not recorded. The clerestory lights stand between the lean-to roofs and the
 * eaves as painted (nieuwekerk-scan, choir: frame rows 561-578); the others fill their walls to about
 * a metre under the eaves, as the painting draws the ambulatory and chapel windows.
 */
export const LIGHTS = {
  clerestory: { sill: 15.8, top: 23, width: 2.4 },
  aisle: { sill: 2.5, top: 9.2, width: 2.4 },
  chapel: { sill: 2.5, top: 9.2, width: 2.2 },
  west: { sill: 7.5, top: 21.5, width: 5.2 },
  transept: { sill: 6, top: 22, width: 6.2 },
  lowFront: { sill: 2.5, top: 12, width: 4.2 },
  portal: { sill: 0, top: 5.5, width: 2.8 },
} as const;

/** A pointed window centred at `centre` along a wall's panel. */
export function lancet(centre: number, lights: Lights): Ring {
  return pointedWindow(centre, lights.sill, lights.width, lights.top - pointedArchRise(lights.width), 3);
}

/**
 * One window in the middle of each bay whose bounds lie along a wall starting at `start` and running
 * in `direction` (1 with u, -1 against it); bays for which `skip` holds get none.
 */
export function bayWindows(bounds: readonly number[], start: number, direction: 1 | -1, lights: Lights, skip: (u0: number, u1: number) => boolean = () => false): Ring[] {
  return bounds.slice(1).flatMap((u1, k) => {
    const u0 = bounds[k]!;
    return skip(u0, u1) ? [] : [lancet(direction * ((u0 + u1) / 2 - start), lights)];
  });
}
